import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';
import { NotificationItem } from '@libs/core/notification/responses/notification.response';
import { TranslatePipe } from '@ngx-translate/core';
import { NotificationStateService } from '@components/notification/services/notification-state.service';
import { NotificationItemComponent } from '../notification-item/notification-item.component';

@Component({
  selector: 'pm-notification-panel',
  imports: [NotificationItemComponent, TranslatePipe],
  template: `
    <div
      class="w-[480px] max-h-[480px] flex flex-col bg-white rounded-xl shadow-lg mt-2"
    >
      <!-- Header -->
      <div class="flex flex-row items-center justify-between px-4 py-3">
        <span class="text-sm font-medium text-content-title leading-[130%]">
          {{ 'NOTIFICATION.NOTIFICATIONS' | translate }}
        </span>
      </div>

      <!-- Content -->
      <div class="overflow-y-auto flex-1" #scrollContainer>
        @if (state.loading() && isEmpty()) {
          <div class="flex items-center justify-center py-8">
            <span class="text-sm text-content-subdued">
              {{ 'NOTIFICATION.LOADING' | translate }}
            </span>
          </div>
        } @else if (isEmpty()) {
          <div class="p-6 text-center text-sm text-content-default">
            {{ 'NOTIFICATION.NO_NOTIFICATIONS' | translate }}
          </div>
        } @else {
          @if (state.notifications().today.length > 0) {
            <div class="flex items-center gap-2.5 px-5 py-1.5 self-stretch">
              <span
                class="text-xs font-medium text-content-subdued text-center leading-[130%]"
              >
                {{ 'NOTIFICATION.TODAY' | translate }}
              </span>
            </div>
            @for (item of state.notifications().today; track item.id) {
              <pm-notification-item
                [notification]="item"
                (clicked)="onItemClick(item)"
              />
            }
          }

          @if (state.notifications().lastWeek.length > 0) {
            <div class="flex items-center gap-2.5 px-5 py-1.5 self-stretch">
              <span
                class="text-xs font-normal text-content-subdued text-center leading-[130%]"
              >
                {{ 'NOTIFICATION.LAST_7_DAYS' | translate }}
              </span>
            </div>
            @for (item of state.notifications().lastWeek; track item.id) {
              <pm-notification-item
                [notification]="item"
                (clicked)="onItemClick(item)"
              />
            }
          }

          @if (state.notifications().older.length > 0) {
            <div class="flex items-center gap-2.5 px-5 py-1.5 self-stretch">
              <span
                class="text-xs font-normal text-content-subdued text-center leading-[130%]"
              >
                {{ 'NOTIFICATION.OLDER' | translate }}
              </span>
            </div>
            @for (item of state.notifications().older; track item.id) {
              <pm-notification-item
                [notification]="item"
                (clicked)="onItemClick(item)"
              />
            }
          }

          @if (state.loading()) {
            <div class="flex items-center justify-center py-3">
              <span class="text-xs text-content-subdued">
                {{ 'NOTIFICATION.LOADING' | translate }}
              </span>
            </div>
          }
        }
      </div>

      <!-- Footer -->
      <div class="py-1"></div>
    </div>
  `,
})
export class NotificationPanelComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  protected readonly state = inject(NotificationStateService);
  readonly closePanel = output<void>();

  @ViewChild('scrollContainer') private readonly scrollContainer!: ElementRef;
  private scrollListener: (() => void) | null = null;

  protected readonly isEmpty = computed(() => {
    const n = this.state.notifications();
    return (
      n.today.length === 0 && n.lastWeek.length === 0 && n.older.length === 0
    );
  });

  ngOnInit(): void {
    this.state.fetchNotifications();
  }

  ngAfterViewInit(): void {
    const el = this.scrollContainer.nativeElement as HTMLElement;
    this.scrollListener = () => this.onScroll(el);
    el.addEventListener('scroll', this.scrollListener, { passive: true });
  }

  ngOnDestroy(): void {
    if (this.scrollListener && this.scrollContainer) {
      (this.scrollContainer.nativeElement as HTMLElement).removeEventListener(
        'scroll',
        this.scrollListener,
      );
    }
  }

  onItemClick(item: NotificationItem): void {
    if (!item.isRead) {
      this.state.markAsRead(item.id);
    }
    this.closePanel.emit();
  }

  private onScroll(el: HTMLElement): void {
    const threshold = 100;
    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < threshold;

    if (nearBottom && this.state.hasMore() && !this.state.loading()) {
      this.state.loadMore();
    }
  }
}
